import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAppointment,
  createAvailability,
  deleteAppointment,
  deleteAvailability,
  fetchAppointments,
  fetchAvailability,
  updateAppointment,
  updateAppointmentStatus,
  respondToAppointment,
} from '../../services/calendarService';
import CreateEventWizard from '../../features/events/components/CreateEventWizard';
import { fetchUsers } from '../../store/slices/userSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const ALL_PARTICIPANT_DMS = '__ALL_PARTICIPANT_DMS__';
const FORM_INPUT = 'w-full rounded-xl border border-violet-200/80 dark:border-violet-700/40 bg-white/90 dark:bg-gray-950/50 px-2.5 py-2 text-sm text-gray-900 dark:text-white placeholder:text-violet-400/70 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60 shadow-sm';
const FORM_LABEL = 'block text-[11px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300 mb-1';
const LINK_LOC_PREFIX = 'Location: ';
const LINK_OTHER_PREFIX = 'Other: ';
const HOUR_HEIGHT = 56;
const WINDOW_HOURS = 12;
const SHIFT_HOURS = 3;
/** Day timeline base hour on the selected date (shift earlier/later from here). */
const DAY_VIEW_START_HOUR = 8;

/** Hours after DAY_VIEW_START so the 12h window begins at the current hour (now pin near top). */
const computeNowWindowOffset = (now = new Date()) => {
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), DAY_VIEW_START_HOUR, 0, 0, 0);
  const diffH = (now.getTime() - dayStart.getTime()) / (60 * 60 * 1000);
  if (diffH <= 0) return 0;
  return Math.floor(diffH);
};

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateStr = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (dateStr, delta) => {
  const d = parseDateStr(dateStr);
  d.setDate(d.getDate() + delta);
  return toDateStr(d);
};

const startOfWeek = (dateStr) => {
  const d = parseDateStr(dateStr);
  d.setDate(d.getDate() - d.getDay());
  return toDateStr(d);
};

const weekDates = (dateStr) => {
  const start = startOfWeek(dateStr);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

/** Normalize browser time values (often HH:mm:ss) to HH:mm */
const normalizeTime = (timeStr) => {
  if (!timeStr) return '00:00';
  const parts = String(timeStr).trim().split(':');
  const h = String(Number(parts[0]) || 0).padStart(2, '0');
  const m = String(Number(parts[1]) || 0).padStart(2, '0');
  return `${h}:${m}`;
};

const minutesFromMidnight = (timeStr) => {
  if (!timeStr) return 0;
  const normalized = normalizeTime(timeStr);
  const [h, m] = normalized.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const formatHourLabel = (hour) => {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

const formatTimeShort = (timeStr) => {
  if (!timeStr) return '';
  const normalized = normalizeTime(timeStr);
  const [hStr, mStr] = normalized.split(':');
  let h = Number(hStr);
  const m = mStr || '00';
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
};

const formatDateTimeLabel = (date) => {
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day} · ${time}`;
};

const minutesToTime = (totalMinutes) => {
  const dayMins = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = Math.floor(dayMins / 60);
  const m = dayMins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const APPT_QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }, { list: 'ordered' }], ['link']],
};

const ordinalDay = (day) => {
  const n = Number(day);
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatPrettyAppointmentDate = (dateStr, today) => {
  const parsed = parseDateStr(dateStr || today);
  const todayParsed = parseDateStr(today);
  const dayNum = parsed.getDate();
  const month = parsed.toLocaleDateString(undefined, { month: 'short' });
  const year = parsed.getFullYear();
  const weekday = parsed.toLocaleDateString(undefined, { weekday: 'long' });
  const heading = year === todayParsed.getFullYear()
    ? `${ordinalDay(dayNum)} ${month}`
    : `${ordinalDay(dayNum)} ${month} ${year}`;
  const diffDays = Math.round((parsed.getTime() - todayParsed.getTime()) / (24 * 60 * 60 * 1000));
  let relative = 'today';
  if (diffDays === 1) relative = 'tomorrow';
  else if (diffDays === -1) relative = 'yesterday';
  else if (diffDays > 1) relative = `after ${diffDays} days`;
  else if (diffDays < -1) relative = `${Math.abs(diffDays)} days ago`;
  return { heading, weekday, relative };
};

const APPT_QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const snapMinutes = (mins, step = 15) => Math.round(mins / step) * step;

const splitAppointmentNotes = (raw) => {
  const lines = String(raw || '').split('\n');
  let locationLink = '';
  let otherLink = '';
  const rest = [];
  lines.forEach((line) => {
    if (line.startsWith(LINK_LOC_PREFIX)) locationLink = line.slice(LINK_LOC_PREFIX.length);
    else if (line.startsWith(LINK_OTHER_PREFIX)) otherLink = line.slice(LINK_OTHER_PREFIX.length);
    else rest.push(line);
  });
  return { description: rest.join('\n').trim(), locationLink, otherLink };
};

const joinAppointmentNotes = (description, locationLink, otherLink) => {
  const parts = [];
  const html = String(description || '').trim();
  const textOnly = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (textOnly) parts.push(html);
  if (String(locationLink || '').trim()) parts.push(`${LINK_LOC_PREFIX}${String(locationLink).trim()}`);
  if (String(otherLink || '').trim()) parts.push(`${LINK_OTHER_PREFIX}${String(otherLink).trim()}`);
  return parts.join('\n');
};

/**
 * Lay out overlapping timed blocks side-by-side with equal width
 * (2 concurrent → 50% each, 3 → ~33%, etc.).
 */
const layoutOverlappingBlocks = (blocks) => {
  if (!blocks?.length) return [];

  const sorted = [...blocks].sort((a, b) => {
    if (a.top !== b.top) return a.top - b.top;
    return b.height - a.height;
  });

  // Cluster into connected overlap groups (touching edges do not overlap)
  const groups = [];
  let group = [];
  let groupEnd = -Infinity;
  for (const block of sorted) {
    const end = block.top + block.height;
    if (group.length && block.top >= groupEnd - 0.5) {
      groups.push(group);
      group = [];
      groupEnd = -Infinity;
    }
    group.push(block);
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length) groups.push(group);

  const laid = [];
  for (const g of groups) {
    const colEnds = [];
    const assigned = g.map((block) => {
      let col = colEnds.findIndex((end) => block.top >= end - 0.5);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(block.top + block.height);
      } else {
        colEnds[col] = block.top + block.height;
      }
      return { block, column: col };
    });
    const totalColumns = Math.max(colEnds.length, 1);
    const widthPct = 100 / totalColumns;
    for (const { block, column } of assigned) {
      laid.push({
        ...block,
        column,
        totalColumns,
        leftPct: column * widthPct,
        widthPct,
      });
    }
  }
  return laid;
};

const blockLayoutStyle = (block) => ({
  top: block.top,
  height: block.height,
  left: `calc(${block.leftPct}% + 1px)`,
  width: `calc(${block.widthPct}% - 2px)`,
});


const toDateTime = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = normalizeTime(timeStr).split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};

const snapDateToQuarter = (date) => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  d.setMinutes(Math.floor(d.getMinutes() / 15) * 15);
  return d;
};

const buildMonthCells = (year, month) => {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const CalendarPanel = ({ channel, otherUser, onClose, personalMode = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels || {});
  const todayStr = useMemo(() => toDateStr(new Date()), []);
  const [viewMode, setViewMode] = useState('day'); // day | week | month
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [availability, setAvailability] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    meetingLink: '',
    locationLink: '',
    otherLink: '',
    recurrenceType: 'none',
    daysOfWeek: [],
    daysOfMonth: [],
  });
  const [formTab, setFormTab] = useState('info');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [participantQuery, setParticipantQuery] = useState('');
  const [showEventWizard, setShowEventWizard] = useState(false);
  const [wizardSchedulePreset, setWizardSchedulePreset] = useState(null);
  const [personalChannelId, setPersonalChannelId] = useState(ALL_PARTICIPANT_DMS);

  const allUsers = useSelector((state) => state.users?.users || []);
  const gridRef = useRef(null);
  const dragRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  /** Shift the 12h window forward/back (0 = from day-start hour; on today, initial = current hour). */
  const [windowOffsetHours, setWindowOffsetHours] = useState(() => computeNowWindowOffset());

  const isPersonal = personalMode || !channel?.id;
  const isDm = !isPersonal && (channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE');

  const calendarChannelOptions = useMemo(() => {
    const list = [...(channels || []), ...(directMessages || [])].filter((ch) => {
      if (!ch?.id) return false;
      const type = ch.type;
      if (type === 'ANNOUNCEMENT') return false;
      if (ch.readOnly) return false;
      const name = (ch.name || '').toLowerCase();
      if (name === 'workloop' || name.includes('workloop')) return false;
      return true;
    });
    return list;
  }, [channels, directMessages]);

  const channelLabel = (ch) => {
    if (!ch) return 'Conversation';
    if (ch.type === 'direct' || ch.type === 'DIRECT_MESSAGE') {
      return ch.name || 'Direct message';
    }
    return `#${ch.name || 'channel'}`;
  };

  // Refresh the "now" line every minute so the pin stays accurate
  useEffect(() => {
    const ONE_MIN_MS = 60 * 1000;
    const id = setInterval(() => setNowTick(Date.now()), ONE_MIN_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isPersonal || draft?.kind !== 'slot') return;
    setDraft((d) => (d ? { ...d, kind: 'appointment', availabilitySlotId: '' } : d));
  }, [isPersonal, draft?.kind]);

  /** Offset so today's current hour sits at the top of the 12h day window. */
  const offsetHoursToShowNow = useCallback(() => computeNowWindowOffset(new Date(nowTick)), [nowTick]);

  const openDayForDate = useCallback((dateStr) => {
    setSelectedDate(dateStr);
    setViewMode('day');
    setWindowOffsetHours(dateStr === todayStr ? offsetHoursToShowNow() : 0);
    setDraft(null);
    setSelectedEvent(null);
  }, [todayStr, offsetHoursToShowNow]);

  const windowStart = useMemo(() => {
    // Always anchor day timeline to the selected calendar date (not wall-clock "now"),
    // so week/month → day navigation and same-day call blocks work.
    const start = parseDateStr(selectedDate);
    start.setHours(DAY_VIEW_START_HOUR, 0, 0, 0);
    start.setTime(start.getTime() + windowOffsetHours * 60 * 60 * 1000);
    return start;
  }, [selectedDate, windowOffsetHours]);

  const windowEnd = useMemo(
    () => new Date(windowStart.getTime() + WINDOW_HOURS * 60 * 60 * 1000),
    [windowStart],
  );

  const hourSlots = useMemo(
    () => Array.from({ length: WINDOW_HOURS }, (_, i) => (
      new Date(windowStart.getTime() + i * 60 * 60 * 1000)
    )),
    [windowStart],
  );

  const nowLineTop = useMemo(() => {
    const nowMs = nowTick;
    if (nowMs < windowStart.getTime() || nowMs > windowEnd.getTime()) return null;
    return ((nowMs - windowStart.getTime()) / (60 * 60 * 1000)) * HOUR_HEIGHT;
  }, [nowTick, windowStart, windowEnd]);

  const partnerName = otherUser
    ? `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim() || otherUser.username
    : (channel?.name ? `#${channel.name}` : 'your schedule');

  const subtitle = isPersonal
    ? 'Your schedule across conversations'
    : (isDm ? `with ${partnerName}` : `in #${channel?.name || 'channel'}`);

  const userLabel = (u) => {
    if (!u) return 'Unknown';
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return name || u.username || 'Unknown';
  };

  const memberIdSet = useMemo(() => {
    // Personal sidebar calendar: invite anyone in the org
    if (isPersonal) {
      return null;
    }
    if (channel?.memberIds) {
      return new Set(channel.memberIds);
    }
    return null;
  }, [isPersonal, channel?.memberIds]);

  const participantCandidates = useMemo(() => {
    const q = participantQuery.trim().toLowerCase();
    const memberIds = memberIdSet ? Array.from(memberIdSet) : null;

    // Channel calendar: members first. Personal calendar: all org users.
    const sourceUsers = memberIds
      ? memberIds
          .filter((id) => id && id !== user?.id)
          .map((id) => {
            const found = (allUsers || []).find((u) => u.id === id);
            return found || { id, firstName: '', lastName: '', username: id };
          })
      : (allUsers || []).filter((u) => u?.id && u.id !== user?.id);

    return sourceUsers
      .filter((u) => {
        if (!q) return true;
        return userLabel(u).toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q);
      })
      .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
  }, [allUsers, user?.id, participantQuery, memberIdSet]);

  const toggleParticipant = (id) => {
    if (!id) return;
    // DM partner stays selected
    if (isDm && id === otherUser?.id) return;
    setSelectedParticipantIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const range = useMemo(() => {
    // Full-day bounds for inclusive between queries (backend normalizes to yyyy-MM-dd)
    if (viewMode === 'day') {
      // Include ±3 days so the day strip can show event dots
      return {
        from: `${addDays(selectedDate, -3)}T00:00:00`,
        to: `${addDays(selectedDate, 3)}T23:59:59`,
      };
    }
    if (viewMode === 'week') {
      const days = weekDates(selectedDate);
      return { from: `${days[0]}T00:00:00`, to: `${days[6]}T23:59:59` };
    }
    const d = parseDateStr(selectedDate);
    const monthStart = toDateStr(new Date(d.getFullYear(), d.getMonth(), 1));
    const monthEnd = toDateStr(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    return {
      from: `${monthStart}T00:00:00`,
      to: `${monthEnd}T23:59:59`,
    };
  }, [viewMode, selectedDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const channelId = isPersonal ? undefined : channel.id;
      const results = await Promise.allSettled([
        fetchAvailability({ channelId, from: range.from, to: range.to }),
        fetchAppointments({ channelId, from: range.from, to: range.to }),
      ]);
      const [slotsResult, apptsResult] = results;
      if (slotsResult.status === 'fulfilled') {
        setAvailability(Array.isArray(slotsResult.value) ? slotsResult.value : []);
      } else {
        setAvailability([]);
      }
      if (apptsResult.status === 'fulfilled') {
        setAppointments(Array.isArray(apptsResult.value) ? apptsResult.value : []);
      } else {
        setAppointments([]);
      }
      const errors = results
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason?.response?.data?.error || r.reason?.message)
        .filter(Boolean);
      if (errors.length > 0) {
        setError(errors[0] || 'Failed to load calendar');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [isPersonal, channel?.id, range.from, range.to]);

  useEffect(() => {
    dispatch(fetchUsers({ force: true }));
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onCalendarChanged = (event) => {
      const date = String(event?.detail?.date || '').slice(0, 10);
      const startTime = String(event?.detail?.startTime || '');
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        setSelectedDate(date);
        if (startTime) {
          const hour = Number(String(startTime).split(':')[0]);
          if (Number.isFinite(hour)) {
            setViewMode('day');
            setWindowOffsetHours(Math.max(0, hour - DAY_VIEW_START_HOUR));
          }
        }
      }
      loadData();
    };
    window.addEventListener('workloop:calendar-changed', onCalendarChanged);
    return () => window.removeEventListener('workloop:calendar-changed', onCalendarChanged);
  }, [loadData]);

  // Keep open event detail in sync when appointments refresh (RSVP updates)
  useEffect(() => {
    if (!selectedEvent || selectedEvent.type !== 'appointment' || !selectedEvent.data?.id) return;
    const fresh = appointments.find((a) => a.id === selectedEvent.data.id);
    if (!fresh) return;
    if (JSON.stringify(fresh) !== JSON.stringify(selectedEvent.data)) {
      setSelectedEvent({ type: 'appointment', data: fresh });
    }
  }, [appointments, selectedEvent]);

  const datesWithEvents = useMemo(() => {
    const set = new Set();
    availability.forEach((s) => set.add(s.date));
    appointments.forEach((a) => {
      if (a.status !== 'CANCELLED') set.add(a.date);
    });
    return set;
  }, [availability, appointments]);

  const msToTop = useCallback((ms) => {
    return ((ms - windowStart.getTime()) / (60 * 60 * 1000)) * HOUR_HEIGHT;
  }, [windowStart]);

  const eventBlocks = useMemo(() => {
    const blocks = [];
    const winStartMs = windowStart.getTime();
    const winEndMs = windowEnd.getTime();

    const pushBlock = (item, type, label, sub, colorClass) => {
      if (!item?.date || !item?.startTime || !item?.endTime) return;
      if (type === 'appointment' && item.status === 'CANCELLED') return;
      const startMs = toDateTime(item.date, item.startTime).getTime();
      let endMs = toDateTime(item.date, item.endTime).getTime();
      if (endMs <= startMs) {
        endMs += 24 * 60 * 60 * 1000;
      }
      if (endMs <= winStartMs || startMs >= winEndMs) return;
      const clippedStart = Math.max(startMs, winStartMs);
      const clippedEnd = Math.min(endMs, winEndMs);
      blocks.push({
        key: `${type}-${item.id}`,
        type,
        data: item,
        top: msToTop(clippedStart),
        height: Math.max(((clippedEnd - clippedStart) / (60 * 60 * 1000)) * HOUR_HEIGHT, 20),
        label,
        sub,
        colorClass,
      });
    };

    appointments.forEach((appt) => {
      pushBlock(
        appt,
        'appointment',
        appt.title,
        `${formatTimeShort(appt.startTime)} – ${formatTimeShort(appt.endTime)}`,
        appt.status === 'CONFIRMED'
          ? 'bg-blue-600 border-blue-700 text-white'
          : 'bg-blue-100 dark:bg-blue-900/70 border-blue-400 dark:border-blue-500 text-blue-900 dark:text-blue-100',
      );
    });

    availability.forEach((slot) => {
      const isMine = slot.userId === user?.id;
      const isBooked = slot.status === 'BOOKED';
      pushBlock(
        slot,
        'slot',
        isMine
          ? (isBooked ? 'My slot · Booked' : 'Available')
          : (isBooked ? 'Booked' : `${slot.userName || partnerName} free`),
        `${formatTimeShort(slot.startTime)} – ${formatTimeShort(slot.endTime)}`,
        isBooked
          ? 'bg-violet-100 dark:bg-violet-900/50 border-violet-300 dark:border-violet-600 text-violet-900 dark:text-violet-100'
          : isMine
            ? 'bg-emerald-100 dark:bg-emerald-900/50 border-emerald-300 dark:border-emerald-600 text-emerald-900 dark:text-emerald-100 border-dashed'
            : 'bg-teal-100 dark:bg-teal-900/50 border-teal-300 dark:border-teal-600 text-teal-900 dark:text-teal-100 border-dashed',
      );
    });

    return blocks;
  }, [appointments, availability, windowStart, windowEnd, msToTop, user?.id, partnerName]);

  const isUserInAppointment = useCallback((appt, userId) => {
    if (!appt || !userId) return false;
    if (appt.organizerId === userId || appt.participantId === userId) return true;
    if ((appt.additionalParticipantIds || []).includes(userId)) return true;
    if ((appt.participants || []).some((p) => p?.id === userId)) return true;
    if ((appt.rsvps || []).some((r) => r?.userId === userId)) return true;
    return false;
  }, []);

  const blocksForUser = useCallback((userId) => (
    eventBlocks.filter((block) => {
      if (!userId) return false;
      if (block.type === 'slot') return block.data?.userId === userId;
      return isUserInAppointment(block.data, userId);
    })
  ), [eventBlocks, isUserInAppointment]);

  const laidOutEventBlocks = useMemo(
    () => layoutOverlappingBlocks(eventBlocks),
    [eventBlocks],
  );

  const laidBlocksForUser = useCallback(
    (userId) => layoutOverlappingBlocks(blocksForUser(userId)),
    [blocksForUser],
  );

  /** Free / Busy for proposed draft window (channel participant picker). */
  const getParticipantBusyStatus = useCallback((userId) => {
    if (!draft || !userId || draft.kind === 'slot') return null;
    const date = draft.date || selectedDate;
    const startMs = toDateTime(date, draft.startTime).getTime();
    let endMs = toDateTime(date, draft.endTime).getTime();
    if (endMs <= startMs) endMs += 24 * 60 * 60 * 1000;
    const overlaps = (a, b) => a < endMs && b > startMs;

    for (const appt of appointments) {
      if (appt.status === 'CANCELLED') continue;
      if (appt.date !== date) continue;
      if (!isUserInAppointment(appt, userId)) continue;
      const aStart = toDateTime(appt.date, appt.startTime).getTime();
      const aEnd = toDateTime(appt.date, appt.endTime).getTime();
      if (overlaps(aStart, aEnd)) return 'Busy';
    }
    for (const slot of availability) {
      if (slot.userId !== userId || slot.status !== 'BOOKED') continue;
      if (slot.date !== date) continue;
      const aStart = toDateTime(slot.date, slot.startTime).getTime();
      const aEnd = toDateTime(slot.date, slot.endTime).getTime();
      if (overlaps(aStart, aEnd)) return 'Busy';
    }
    return 'Free';
  }, [draft, selectedDate, appointments, availability, isUserInAppointment]);

  const weekDays = useMemo(() => weekDates(selectedDate), [selectedDate]);

  const monthCells = useMemo(() => {
    const d = parseDateStr(selectedDate);
    return buildMonthCells(d.getFullYear(), d.getMonth());
  }, [selectedDate]);

  const yToOffsetMinutes = (clientY) => {
    const el = gridRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top + el.scrollTop;
    return snapMinutes(Math.max(0, Math.min(WINDOW_HOURS * 60, (y / HOUR_HEIGHT) * 60)));
  };

  const offsetToDateTime = (offsetMins) => {
    const t = new Date(windowStart.getTime() + offsetMins * 60 * 1000);
    return {
      date: toDateStr(t),
      time: minutesToTime(t.getHours() * 60 + t.getMinutes()),
    };
  };

  const openDraft = (startTime, endTime, kind = 'appointment', slotId = '', date = toDateStr(windowStart)) => {
    setSelectedEvent(null);
    setDraft({ startTime, endTime, kind, availabilitySlotId: slotId, date, appointmentId: null });
    setForm({
      title: kind === 'appointment' ? '' : 'Available',
      description: '',
      meetingLink: '',
      locationLink: '',
      otherLink: '',
      recurrenceType: 'none',
      daysOfWeek: [],
      daysOfMonth: [],
    });
    setFormTab('info');
    setParticipantQuery('');
    setSelectedParticipantIds(otherUser?.id ? [otherUser.id] : []);
    if (isPersonal) {
      setPersonalChannelId(ALL_PARTICIPANT_DMS);
    }
  };

  const openEditAppointment = (appt) => {
    if (!appt?.id) return;
    setSelectedEvent(null);
    setDraft({
      startTime: normalizeTime(appt.startTime),
      endTime: normalizeTime(appt.endTime),
      kind: 'edit',
      availabilitySlotId: '',
      date: appt.date || selectedDate,
      appointmentId: appt.id,
    });
    const notes = splitAppointmentNotes(appt.description || '');
    setForm({
      title: appt.title || '',
      description: notes.description,
      meetingLink: appt.meetingLink || '',
      locationLink: notes.locationLink,
      otherLink: notes.otherLink,
      recurrenceType: appt.recurrenceType || 'none',
      daysOfWeek: Array.isArray(appt.recurrenceDaysOfWeek) ? appt.recurrenceDaysOfWeek : [],
      daysOfMonth: Array.isArray(appt.recurrenceDaysOfMonth) ? appt.recurrenceDaysOfMonth : [],
    });
    setFormTab('info');
    setParticipantQuery('');
    const ids = new Set();
    if (otherUser?.id) ids.add(otherUser.id);
    if (appt.participantId && appt.participantId !== user?.id) ids.add(appt.participantId);
    (appt.participants || []).forEach((p) => {
      if (p?.id && p.id !== user?.id) ids.add(p.id);
    });
    setSelectedParticipantIds(Array.from(ids));
    if (appt.postToParticipantDms || !appt.channelId) setPersonalChannelId(ALL_PARTICIPANT_DMS);
    else setPersonalChannelId(appt.channelId);
    if (appt.date) {
      setSelectedDate(appt.date);
      setWindowOffsetHours(appt.date === todayStr ? offsetHoursToShowNow() : 0);
    }
    setViewMode('day');
  };

  const handleGridMouseDown = (e) => {
    if (viewMode !== 'day' || e.button !== 0) return;
    if (e.target.closest('[data-cal-event]')) return;
    const startMins = yToOffsetMinutes(e.clientY);
    dragRef.current = { startMins, currentMins: startMins + 30 };
    setDragPreview({
      top: (startMins / 60) * HOUR_HEIGHT,
      height: (30 / 60) * HOUR_HEIGHT,
    });

    const onMove = (ev) => {
      if (!dragRef.current) return;
      let current = yToOffsetMinutes(ev.clientY);
      if (current <= dragRef.current.startMins) current = dragRef.current.startMins + 15;
      dragRef.current.currentMins = current;
      const topMins = Math.min(dragRef.current.startMins, current);
      const endMins = Math.max(dragRef.current.startMins, current);
      setDragPreview({
        top: (topMins / 60) * HOUR_HEIGHT,
        height: Math.max(((endMins - topMins) / 60) * HOUR_HEIGHT, 14),
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!dragRef.current) return;
      const start = Math.min(dragRef.current.startMins, dragRef.current.currentMins);
      let end = Math.max(dragRef.current.startMins, dragRef.current.currentMins);
      if (end - start < 15) end = Math.min(start + 30, WINDOW_HOURS * 60);
      dragRef.current = null;
      setDragPreview(null);
      const startDt = offsetToDateTime(start);
      const endDt = offsetToDateTime(end);
      if (startDt.date === endDt.date && endDt.time > startDt.time) {
        openDraft(startDt.time, endDt.time, 'appointment', '', startDt.date);
      } else if (startDt.date === endDt.date) {
        openDraft(startDt.time, minutesToTime(minutesFromMidnight(startDt.time) + 30), 'appointment', '', startDt.date);
      } else {
        // Window crossed midnight — keep booking on the start day through late evening
        openDraft(startDt.time, '23:45', 'appointment', '', startDt.date);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleEventClick = (block, e) => {
    e.stopPropagation();
    setDraft(null);
    if (block.type === 'appointment' && block.data?.id) {
      const fresh = appointments.find((a) => a.id === block.data.id) || block.data;
      setSelectedEvent({ type: block.type, data: fresh });
      return;
    }
    setSelectedEvent({ type: block.type, data: block.data });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      const date = draft.date || selectedDate;
      const startTime = normalizeTime(draft.startTime);
      const endTime = normalizeTime(draft.endTime);
      const extraParticipantIds = selectedParticipantIds.filter(
        (id) => id && id !== user?.id && !(isDm && id === otherUser?.id),
      );
      const composedDescription = joinAppointmentNotes(form.description, form.locationLink, form.otherLink);

      if (draft.kind !== 'slot' && !form.title.trim()) {
        setFormTab('info');
        throw new Error('Add a title');
      }

      if (draft.kind === 'slot') {
        const created = await createAvailability({
          date,
          startTime,
          endTime,
          timezone: tz,
        });
        setAvailability((prev) => {
          const next = prev.filter((s) => s.id !== created.id);
          return [...next, created];
        });
        if (date !== selectedDate) setSelectedDate(date);
      } else if (draft.kind === 'edit' && draft.appointmentId) {
        const updated = await updateAppointment(draft.appointmentId, {
          title: form.title.trim() || 'Meeting',
          description: composedDescription,
          date,
          startTime,
          endTime,
          meetingLink: form.meetingLink || '',
          additionalParticipantIds: extraParticipantIds,
          timezone: tz,
        });
        setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
        if (date !== selectedDate) setSelectedDate(date);
      } else {
        const postToParticipantDms = isPersonal && personalChannelId === ALL_PARTICIPANT_DMS;
        const targetChannelId = isPersonal
          ? (postToParticipantDms ? undefined : personalChannelId)
          : channel?.id;
        if (postToParticipantDms && extraParticipantIds.length === 0) {
          setFormTab('participants');
          throw new Error('Select at least one participant to post in their DMs');
        }
        if (!postToParticipantDms && !targetChannelId) {
          setFormTab('participants');
          throw new Error(isPersonal
            ? 'Choose a channel or DM to post this meeting'
            : 'Channel is required');
        }
        const created = await createAppointment({
          channelId: targetChannelId,
          title: form.title.trim() || 'Meeting',
          description: composedDescription,
          date,
          startTime,
          endTime,
          meetingLink: form.meetingLink || undefined,
          availabilitySlotId: draft.availabilitySlotId || undefined,
          additionalParticipantIds: extraParticipantIds,
          timezone: tz,
          postToParticipantDms: postToParticipantDms || undefined,
          recurrenceType: draft.availabilitySlotId ? 'none' : (form.recurrenceType || 'none'),
          recurrenceDaysOfWeek: !draft.availabilitySlotId && form.recurrenceType === 'weekly' ? form.daysOfWeek : undefined,
          recurrenceDaysOfMonth: !draft.availabilitySlotId && form.recurrenceType === 'monthly' ? form.daysOfMonth : undefined,
        });
        setAppointments((prev) => {
          const next = prev.filter((a) => a.id !== created.id);
          return [...next, created];
        });
        if (date !== selectedDate) setSelectedDate(date);
      }
      setDraft(null);
      setViewMode('day');
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBookPartnerSlot = (slot) => {
    openDraft(slot.startTime, slot.endTime, 'appointment', slot.id, slot.date);
  };

  const handleDeleteSlot = async (id) => {
    try {
      await deleteAvailability(id);
      setSelectedEvent(null);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to remove slot');
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await updateAppointmentStatus(id, status);
      setSelectedEvent(null);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update');
    }
  };

  const handleRsvp = async (id, status) => {
    try {
      const updated = await respondToAppointment(id, status);
      setSelectedEvent({ type: 'appointment', data: updated });
      setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update RSVP');
    }
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await deleteAppointment(id);
      setSelectedEvent(null);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
    }
  };

  const goToday = () => {
    setSelectedDate(todayStr);
    setViewMode('day');
    setWindowOffsetHours(offsetHoursToShowNow());
    setNowTick(Date.now());
    setDraft(null);
    setSelectedEvent(null);
  };

  const goPrev = () => {
    if (viewMode === 'day') {
      // Move to previous calendar day and reset to day-start window
      setSelectedDate((prev) => addDays(prev, -1));
      setWindowOffsetHours(0);
    } else if (viewMode === 'week') setSelectedDate(addDays(selectedDate, -7));
    else {
      const d = parseDateStr(selectedDate);
      setSelectedDate(toDateStr(new Date(d.getFullYear(), d.getMonth() - 1, 1)));
    }
  };

  const goNext = () => {
    if (viewMode === 'day') {
      setSelectedDate((prev) => addDays(prev, 1));
      setWindowOffsetHours(0);
    } else if (viewMode === 'week') setSelectedDate(addDays(selectedDate, 7));
    else {
      const d = parseDateStr(selectedDate);
      setSelectedDate(toDateStr(new Date(d.getFullYear(), d.getMonth() + 1, 1)));
    }
  };

  const shiftTimelineUp = () => setWindowOffsetHours((h) => h - SHIFT_HOURS);
  const shiftTimelineDown = () => setWindowOffsetHours((h) => h + SHIFT_HOURS);

  const headerLabel = useMemo(() => {
    if (viewMode === 'day') {
      const sameDay = toDateStr(windowStart) === toDateStr(windowEnd);
      if (sameDay) {
        return `${windowStart.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${windowStart.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} – ${windowEnd.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
      }
      return `${formatDateTimeLabel(windowStart)} → ${formatDateTimeLabel(windowEnd)}`;
    }
    const d = parseDateStr(selectedDate);
    if (viewMode === 'week') {
      const days = weekDates(selectedDate);
      const a = parseDateStr(days[0]);
      const b = parseDateStr(days[6]);
      const sameMonth = a.getMonth() === b.getMonth();
      if (sameMonth) {
        return `${a.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} – ${b.getDate()}, ${b.getFullYear()}`;
      }
      return `${a.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${b.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [viewMode, selectedDate, windowStart, windowEnd]);

  const eventsForDate = (dateStr) => {
    const appts = appointments.filter((a) => a.date === dateStr && a.status !== 'CANCELLED');
    const slots = availability.filter((s) => s.date === dateStr);
    return { appts, slots };
  };

  /** 3 days before selectedDate, selectedDate, 3 days after — for day-view quick nav */
  const dayStripDates = useMemo(() => (
    Array.from({ length: 7 }, (_, i) => addDays(selectedDate, i - 3))
  ), [selectedDate]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {isPersonal ? 'My Calendar' : 'Calendar'}
          </h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* View switcher: Day → Week → Month */}
      <div className="px-3 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700/80 flex-shrink-0 space-y-2">
        <div className="flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-900">
          {[
            { id: 'day', label: 'Day' },
            { id: 'week', label: 'Week' },
            { id: 'month', label: 'Month' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                setViewMode(opt.id);
                if (opt.id === 'day') {
                  // Keep selectedDate — do not jump back to today
                  setWindowOffsetHours(
                    selectedDate === todayStr ? offsetHoursToShowNow() : 0,
                  );
                  setNowTick(Date.now());
                }
                setDraft(null);
                setSelectedEvent(null);
              }}
              className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors ${
                viewMode === opt.id
                  ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-1">
          {viewMode === 'day' ? (
            <>
              <button
                type="button"
                onClick={shiftTimelineUp}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                aria-label="Earlier by 3 hours"
                title="Earlier · 3 hours"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <div className="text-center min-w-0 flex-1 px-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{headerLabel}</p>
                {loading && <p className="text-[10px] text-gray-400">Loading…</p>}
              </div>
              <button
                type="button"
                onClick={shiftTimelineDown}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                aria-label="Later by 3 hours"
                title="Later · 3 hours"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                aria-label="Previous"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="text-center min-w-0 flex-1 px-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{headerLabel}</p>
                {loading && <p className="text-[10px] text-gray-400">Loading…</p>}
              </div>
              <button
                type="button"
                onClick={goNext}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                aria-label="Next"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          {viewMode !== 'day' || windowOffsetHours !== 0 || selectedDate !== todayStr ? (
            <button
              type="button"
              onClick={goToday}
              className="ml-0.5 px-2 py-1 text-[11px] font-semibold rounded-md border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Today
            </button>
          ) : null}
        </div>

        {viewMode === 'day' && (
          <div className="grid grid-cols-7 gap-1">
            {dayStripDates.map((dateStr) => {
              const d = parseDateStr(dateStr);
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === todayStr;
              const { appts, slots } = eventsForDate(dateStr);
              const hasItems = appts.length + slots.length > 0;
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => openDayForDate(dateStr)}
                  className={`relative flex flex-col items-center justify-center rounded-xl px-0.5 py-1.5 transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-sm'
                      : isToday
                        ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
                        : 'bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 border border-transparent'
                  }`}
                  title={d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                  aria-current={isSelected ? 'date' : undefined}
                >
                  <span className={`text-[9px] font-semibold uppercase tracking-wide ${
                    isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-gray-500'
                  }`}
                  >
                    {WEEKDAY_SHORT[d.getDay()]}
                  </span>
                  <span className="text-sm font-bold tabular-nums leading-tight mt-0.5">
                    {d.getDate()}
                  </span>
                  <span className={`text-[9px] font-medium leading-tight ${
                    isSelected ? 'text-blue-100/90' : 'text-gray-400 dark:text-gray-500'
                  }`}
                  >
                    {d.toLocaleDateString(undefined, { month: 'short' })}
                  </span>
                  {hasItems && (
                    <span
                      className={`absolute bottom-1 w-1 h-1 rounded-full ${
                        isSelected ? 'bg-white' : 'bg-blue-500'
                      }`}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {viewMode === 'day' && (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => {
                const start = offsetToDateTime(0);
                const end = offsetToDateTime(30);
                if (isPersonal) {
                  openDraft(start.time, end.time, 'appointment', '', start.date);
                } else {
                  openDraft(start.time, end.time, 'slot', '', start.date);
                }
              }}
              className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-md ${
                isPersonal
                  ? 'text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600'
                  : 'border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'
              }`}
            >
              {isPersonal ? '+ Appointment' : '+ Availability'}
            </button>
            <button
              type="button"
              onClick={() => {
                const start = offsetToDateTime(0);
                const end = offsetToDateTime(30);
                setWizardSchedulePreset({
                  startDate: start.date,
                  startTime: start.time,
                  endDate: start.date,
                  endTime: end.time,
                });
                setShowEventWizard(true);
              }}
              className="flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              + Event
            </button>
          </div>
        )}
        {viewMode === 'day' && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center">
            Showing 12 hours · ↑ / ↓ moves by 3 hours
          </p>
        )}
      </div>

      {error && (
        <div className="mx-3 mt-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex-shrink-0">
          {error}
        </div>
      )}

      {/* Day body: form 60% | timeline 40% when creating/viewing an event */}
      {viewMode === 'day' && (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {(draft || selectedEvent) && (
            <div className="w-[60%] flex-shrink-0 border-r border-violet-200/70 dark:border-violet-800/40 bg-gradient-to-br from-violet-100 via-sky-50 to-fuchsia-100 dark:from-violet-950/70 dark:via-slate-900 dark:to-fuchsia-950/40 p-3 flex flex-col min-h-0 overflow-hidden relative">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-violet-500 via-sky-400 to-fuchsia-500" aria-hidden="true" />
          {draft && (
            <form onSubmit={handleSave} className="flex flex-col min-h-0 h-full pt-1">
              <div className="flex items-center justify-between gap-2 flex-shrink-0 mb-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-600 dark:text-fuchsia-300">
                    My Calendar
                  </p>
                  <p className="text-sm font-bold bg-gradient-to-r from-violet-700 via-indigo-600 to-fuchsia-600 dark:from-violet-200 dark:via-sky-200 dark:to-fuchsia-200 bg-clip-text text-transparent">
                    {draft.kind === 'slot'
                      ? 'Add availability'
                      : draft.kind === 'edit'
                        ? 'Edit meeting'
                        : 'New appointment'}
                  </p>
                </div>
                <button type="button" onClick={() => setDraft(null)} className="text-violet-400 hover:text-violet-700 dark:hover:text-violet-200">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {draft.kind !== 'edit' && !isPersonal && (
              <div className="flex gap-1 p-0.5 rounded-xl bg-white/50 dark:bg-gray-950/40 border border-white/70 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, kind: 'appointment' }))}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-lg ${
                    draft.kind === 'appointment'
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-sm'
                      : 'text-violet-700 dark:text-violet-200'
                  }`}
                >
                  Appointment
                </button>
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, kind: 'slot', availabilitySlotId: '' }))}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-lg ${
                    draft.kind === 'slot'
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm'
                      : 'text-emerald-700 dark:text-emerald-200'
                  }`}
                >
                  My availability
                </button>
              </div>
              )}

              {draft.kind === 'slot' && (
                <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pt-1">
                  <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-100 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/40 rounded-xl px-3 py-2 border border-emerald-200/70 dark:border-emerald-800/40">
                    {formatTimeShort(draft.startTime)} – {formatTimeShort(draft.endTime)}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="min-w-0">
                      <span className={FORM_LABEL}>Start</span>
                      <input type="time" value={draft.startTime} onChange={(e) => setDraft((d) => ({ ...d, startTime: e.target.value }))} className={FORM_INPUT} />
                    </label>
                    <label className="min-w-0">
                      <span className={FORM_LABEL}>End</span>
                      <input type="time" value={draft.endTime} onChange={(e) => setDraft((d) => ({ ...d, endTime: e.target.value }))} className={FORM_INPUT} />
                    </label>
                  </div>
                </div>
              )}

              {(draft.kind === 'appointment' || draft.kind === 'edit') && (
                <>
                  <div className="flex-shrink-0 grid grid-cols-3 gap-1 p-1 rounded-2xl bg-white/60 dark:bg-gray-950/40 border border-white/80 dark:border-white/10 mb-2 shadow-sm">
                    {[
                      { id: 'info', label: 'Info', active: 'bg-gradient-to-r from-violet-600 to-indigo-500 text-white' },
                      { id: 'participants', label: 'Participants', active: 'bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white' },
                      { id: 'meeting', label: 'Meeting', active: 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setFormTab(tab.id)}
                        className={`py-1.5 text-[11px] font-bold rounded-xl ${
                          formTab === tab.id
                            ? `${tab.active} shadow-sm`
                            : 'text-violet-700/70 dark:text-violet-200/70 hover:bg-white/50 dark:hover:bg-white/5'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-0.5">
                    {formTab === 'info' && (
                      <div className="space-y-2 rounded-2xl bg-gradient-to-br from-white/90 via-violet-50/80 to-sky-50/90 dark:from-gray-950/70 dark:via-violet-950/40 dark:to-slate-900/70 border border-white/80 dark:border-violet-800/40 p-2.5 shadow-sm">
                        <label>
                          <span className={FORM_LABEL}>Title</span>
                          <input
                            type="text"
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Meeting title"
                            className={`${FORM_INPUT} font-medium`}
                            autoFocus
                          />
                        </label>
                        {(() => {
                          const pretty = formatPrettyAppointmentDate(draft.date || selectedDate, todayStr);
                          const shiftDate = (delta) => {
                            setDraft((d) => ({
                              ...d,
                              date: addDays(d.date || selectedDate, delta),
                            }));
                          };
                          const setEndFromStart = (mins) => {
                            setDraft((d) => {
                              const startM = minutesFromMidnight(d.startTime);
                              const nextEnd = Math.min(startM + mins, (23 * 60) + 59);
                              return { ...d, endTime: minutesToTime(Math.max(startM + 1, nextEnd)) };
                            });
                          };
                          return (
                            <div className="flex items-stretch gap-2 min-w-0">
                              <div className="min-w-0 flex-[1.5] flex flex-col">
                                <span className={FORM_LABEL}>Date</span>
                                <div className="relative flex-1 overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-500 to-rose-500 p-[1px] shadow-md min-h-[4.5rem]">
                                  <div className="h-full rounded-[0.65rem] bg-gradient-to-br from-violet-500/95 via-fuchsia-500/90 to-amber-400/90 px-1.5 py-2 text-white flex items-center gap-0.5">
                                    <button
                                      type="button"
                                      onClick={() => shiftDate(-1)}
                                      title="Previous day"
                                      className="shrink-0 rounded-md px-1.5 py-2 text-white/90 hover:bg-white/20 hover:text-white text-lg font-bold leading-none"
                                    >
                                      ‹
                                    </button>
                                    <div className="flex-1 min-w-0 text-center">
                                      <p className="text-lg font-bold leading-tight tracking-tight drop-shadow-sm truncate">
                                        {pretty.heading}
                                      </p>
                                      <p className="text-xs font-medium text-white/95 leading-snug mt-0.5 truncate">
                                        {pretty.weekday}
                                        <span className="text-white/85"> ({pretty.relative})</span>
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => shiftDate(1)}
                                      title="Next day"
                                      className="shrink-0 rounded-md px-1.5 py-2 text-white/90 hover:bg-white/20 hover:text-white text-lg font-bold leading-none"
                                    >
                                      ›
                                    </button>
                                    <span className="relative shrink-0 rounded-md bg-white/20 hover:bg-white/30 p-1.5" title="Change date">
                                      <svg className="w-3.5 h-3.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                                      </svg>
                                      <input
                                        type="date"
                                        aria-label="Change date"
                                        value={draft.date || selectedDate}
                                        onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                                      />
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <label className="min-w-0 flex-1 flex flex-col">
                                <span className={FORM_LABEL}>Start</span>
                                <input
                                  type="time"
                                  value={draft.startTime}
                                  onChange={(e) => setDraft((d) => ({ ...d, startTime: e.target.value }))}
                                  className={`${FORM_INPUT} appt-time-input flex-1 min-h-[4.5rem] text-xl font-bold text-center`}
                                />
                              </label>
                              <div className="flex flex-col items-center justify-center gap-1 shrink-0 pt-4">
                                {[
                                  { mins: 15, label: '+15m' },
                                  { mins: 30, label: '+30m' },
                                  { mins: 60, label: '1h+' },
                                ].map((chip) => (
                                  <button
                                    key={chip.label}
                                    type="button"
                                    onClick={() => setEndFromStart(chip.mins)}
                                    className="text-[10px] font-bold leading-none text-fuchsia-600 dark:text-fuchsia-300 hover:text-violet-700 dark:hover:text-violet-200 underline-offset-2 hover:underline"
                                  >
                                    {chip.label}
                                  </button>
                                ))}
                              </div>
                              <label className="min-w-0 flex-1 flex flex-col">
                                <span className={FORM_LABEL}>End</span>
                                <input
                                  type="time"
                                  value={draft.endTime}
                                  onChange={(e) => setDraft((d) => ({ ...d, endTime: e.target.value }))}
                                  className={`${FORM_INPUT} appt-time-input flex-1 min-h-[4.5rem] text-xl font-bold text-center`}
                                />
                              </label>
                            </div>
                          );
                        })()}
                        <div>
                          <span className={FORM_LABEL}>Description</span>
                          <div className="appt-quill overflow-hidden rounded-xl border border-violet-200/80 dark:border-violet-700/40 bg-white/90 dark:bg-gray-950/50 shadow-sm">
                            <ReactQuill
                              theme="snow"
                              value={form.description || ''}
                              onChange={(html) => setForm((f) => ({ ...f, description: html }))}
                              modules={APPT_QUILL_MODULES}
                              formats={APPT_QUILL_FORMATS}
                              placeholder="What is this about?"
                            />
                          </div>
                        </div>
                        {draft.kind === 'appointment' && !draft.availabilitySlotId && (
                          <div className="space-y-2">
                            <label>
                              <span className={FORM_LABEL}>Frequency</span>
                              <select
                                value={form.recurrenceType || 'none'}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setForm((f) => {
                                    const patch = { ...f, recurrenceType: next };
                                    if (next === 'weekly' && !(f.daysOfWeek || []).length) {
                                      const raw = draft.date || selectedDate;
                                      const parsed = raw ? new Date(`${raw}T00:00:00`) : new Date();
                                      const map = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
                                      patch.daysOfWeek = [map[parsed.getDay()]];
                                    }
                                    if (next === 'monthly' && !(f.daysOfMonth || []).length) {
                                      const raw = draft.date || selectedDate;
                                      const day = raw ? Number(raw.slice(8, 10)) : new Date().getDate();
                                      patch.daysOfMonth = [day];
                                    }
                                    return patch;
                                  });
                                }}
                                className={FORM_INPUT}
                              >
                                <option value="none">Does not repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            </label>
                            {form.recurrenceType === 'weekly' && (
                              <div>
                                <span className={FORM_LABEL}>Days</span>
                                <div className="flex flex-wrap gap-1">
                                  {DAYS_OF_WEEK.map((day) => {
                                    const selected = (form.daysOfWeek || []).includes(day);
                                    return (
                                      <button
                                        key={day}
                                        type="button"
                                        onClick={() => setForm((f) => ({
                                          ...f,
                                          daysOfWeek: selected
                                            ? (f.daysOfWeek || []).filter((item) => item !== day)
                                            : [...(f.daysOfWeek || []), day],
                                        }))}
                                        className={`min-w-[2.4rem] px-2 py-1 text-[11px] rounded-lg font-semibold ${
                                          selected
                                            ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-sm'
                                            : 'bg-white/80 dark:bg-gray-800 text-violet-700 dark:text-violet-200 border border-violet-100 dark:border-violet-800'
                                        }`}
                                      >
                                        {day}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {form.recurrenceType === 'monthly' && (
                              <div>
                                <span className={FORM_LABEL}>Days</span>
                                <div className="grid grid-cols-7 gap-1">
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                                    const selected = (form.daysOfMonth || []).includes(day);
                                    return (
                                      <button
                                        key={day}
                                        type="button"
                                        onClick={() => setForm((f) => ({
                                          ...f,
                                          daysOfMonth: selected
                                            ? (f.daysOfMonth || []).filter((item) => item !== day)
                                            : [...(f.daysOfMonth || []), day],
                                        }))}
                                        className={`h-7 text-[10px] rounded-md font-semibold ${
                                          selected
                                            ? 'bg-gradient-to-br from-fuchsia-500 to-violet-600 text-white'
                                            : 'bg-white/80 dark:bg-gray-800 text-violet-700 dark:text-violet-200'
                                        }`}
                                      >
                                        {day}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {formTab === 'participants' && (
                      <div className="space-y-3 rounded-2xl bg-gradient-to-br from-white/90 via-fuchsia-50/80 to-rose-50/90 dark:from-gray-950/70 dark:via-fuchsia-950/30 dark:to-slate-900/70 border border-white/80 dark:border-fuchsia-800/40 p-3 shadow-sm">
                        {isPersonal && draft.kind === 'appointment' && (
                          <div className="space-y-2">
                            <span className={FORM_LABEL}>Invitation type</span>
                            <div className="grid grid-cols-2 gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  if (personalChannelId === ALL_PARTICIPANT_DMS) {
                                    setPersonalChannelId(calendarChannelOptions[0]?.id || '');
                                  }
                                }}
                                className={`px-2.5 py-2 rounded-xl text-[11px] font-semibold border ${
                                  personalChannelId !== ALL_PARTICIPANT_DMS
                                    ? 'border-transparent bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-sm'
                                    : 'border-fuchsia-200 dark:border-fuchsia-800 text-fuchsia-800 dark:text-fuchsia-200 bg-white/70 dark:bg-gray-900/40'
                                }`}
                              >
                                Conversation
                              </button>
                              <button
                                type="button"
                                onClick={() => setPersonalChannelId(ALL_PARTICIPANT_DMS)}
                                className={`px-2.5 py-2 rounded-xl text-[11px] font-semibold border ${
                                  personalChannelId === ALL_PARTICIPANT_DMS
                                    ? 'border-transparent bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white shadow-sm'
                                    : 'border-fuchsia-200 dark:border-fuchsia-800 text-fuchsia-800 dark:text-fuchsia-200 bg-white/70 dark:bg-gray-900/40'
                                }`}
                              >
                                All participant DMs
                              </button>
                            </div>
                            {personalChannelId !== ALL_PARTICIPANT_DMS && (
                              <label>
                                <span className={FORM_LABEL}>Post meeting in</span>
                                <select
                                  value={personalChannelId}
                                  onChange={(e) => setPersonalChannelId(e.target.value)}
                                  className={FORM_INPUT}
                                >
                                  <option value="">Select channel or DM…</option>
                                  {calendarChannelOptions.map((ch) => (
                                    <option key={ch.id} value={ch.id}>{channelLabel(ch)}</option>
                                  ))}
                                </select>
                              </label>
                            )}
                          </div>
                        )}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className={FORM_LABEL + ' mb-0'}>Participants</span>
                            {!isDm && participantCandidates.length > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const allIds = participantCandidates.map((u) => u.id);
                                  const allSelected = allIds.every((id) => selectedParticipantIds.includes(id));
                                  setSelectedParticipantIds(allSelected ? [] : allIds);
                                }}
                                className="text-[11px] font-semibold text-fuchsia-600 dark:text-fuchsia-300"
                              >
                                {participantCandidates.every((u) => selectedParticipantIds.includes(u.id))
                                  ? 'Clear all'
                                  : 'Select all'}
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 min-h-[1.75rem]">
                            {selectedParticipantIds.map((id) => {
                              const u = allUsers.find((x) => x.id === id)
                                || participantCandidates.find((x) => x.id === id)
                                || (otherUser?.id === id ? otherUser : null);
                              const isPartner = isDm && id === otherUser?.id;
                              return (
                                <span
                                  key={id}
                                  className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white px-2 py-0.5 text-[11px] font-medium shadow-sm"
                                >
                                  {userLabel(u)}
                                  {!isPartner && (
                                    <button
                                      type="button"
                                      onClick={() => toggleParticipant(id)}
                                      className="text-white/80 hover:text-white"
                                      aria-label="Remove participant"
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                            {selectedParticipantIds.length === 0 && (
                              <span className="text-[11px] text-gray-400">No one selected yet</span>
                            )}
                          </div>
                          <input
                            type="text"
                            value={participantQuery}
                            onChange={(e) => setParticipantQuery(e.target.value)}
                            placeholder={isDm ? 'Add people…' : 'Search people…'}
                            className={FORM_INPUT}
                          />
                          {(participantQuery.trim() || !isDm) && (
                            <div className="max-h-36 overflow-y-auto rounded-xl border border-fuchsia-200/70 dark:border-fuchsia-800/40 divide-y divide-fuchsia-100 dark:divide-fuchsia-900/40 bg-white/70 dark:bg-gray-950/40">
                              {participantCandidates.length === 0 ? (
                                <p className="px-2.5 py-2 text-[11px] text-gray-400">No users found</p>
                              ) : (
                                participantCandidates.map((u) => {
                                  const selected = selectedParticipantIds.includes(u.id);
                                  const busyStatus = !isDm ? getParticipantBusyStatus(u.id) : null;
                                  return (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => toggleParticipant(u.id)}
                                      className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/30 flex items-center justify-between gap-2 ${
                                        selected ? 'text-fuchsia-800 dark:text-fuchsia-200 bg-gradient-to-r from-fuchsia-100/80 to-violet-100/80 dark:from-fuchsia-950/40 dark:to-violet-950/30' : 'text-gray-800 dark:text-gray-200'
                                      }`}
                                    >
                                      <span className="truncate min-w-0">
                                        {userLabel(u)}
                                        {isDm && u.id === otherUser?.id ? ' (DM)' : ''}
                                      </span>
                                      <span className="flex-shrink-0 flex items-center gap-1.5">
                                        <span className="text-[10px] font-semibold">{selected ? '✓' : '+'}</span>
                                        {busyStatus && (
                                          <span
                                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                              busyStatus === 'Busy'
                                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                                                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                                            }`}
                                          >
                                            {busyStatus}
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {formTab === 'meeting' && (
                      <div className="space-y-3 rounded-2xl bg-gradient-to-br from-white/90 via-sky-50/90 to-cyan-50/90 dark:from-gray-950/70 dark:via-sky-950/30 dark:to-slate-900/70 border border-white/80 dark:border-sky-800/40 p-3 shadow-sm">
                        <label>
                          <span className={FORM_LABEL}>Meeting link</span>
                          <input
                            type="url"
                            value={form.meetingLink}
                            onChange={(e) => setForm((f) => ({ ...f, meetingLink: e.target.value }))}
                            placeholder="https://meet.google.com/…"
                            className={FORM_INPUT}
                          />
                        </label>
                        <label>
                          <span className={FORM_LABEL}>Location / map</span>
                          <input
                            type="url"
                            value={form.locationLink}
                            onChange={(e) => setForm((f) => ({ ...f, locationLink: e.target.value }))}
                            placeholder="Maps or venue link"
                            className={FORM_INPUT}
                          />
                        </label>
                        <label>
                          <span className={FORM_LABEL}>Other link</span>
                          <input
                            type="url"
                            value={form.otherLink}
                            onChange={(e) => setForm((f) => ({ ...f, otherLink: e.target.value }))}
                            placeholder="Agenda, docs, or extra URL"
                            className={FORM_INPUT}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={saving}
                className="mt-2 flex-shrink-0 w-full py-2 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-violet-600 via-fuchsia-500 to-rose-500 hover:from-violet-700 hover:via-fuchsia-600 hover:to-rose-600 disabled:opacity-60 shadow-md"
              >
                {saving
                  ? 'Saving…'
                  : draft.kind === 'slot'
                    ? 'Save availability'
                    : draft.kind === 'edit'
                      ? 'Save changes'
                      : 'Save appointment'}
              </button>
            </form>
          )}

          {selectedEvent && !draft && (
            <div className="flex-1 min-h-0 overflow-y-auto">
          {(() => {
            const { type, data } = selectedEvent;
            if (type === 'appointment') {
              const sameId = (a, b) => a != null && b != null && String(a) === String(b);
              const isParticipant = sameId(data.participantId, user?.id)
                || (data.rsvps || []).some((r) => sameId(r.userId, user?.id))
                || (data.participants || []).some((p) => sameId(p.id, user?.id));
              const isOrganizer = sameId(data.organizerId, user?.id);
              const rsvpRows = (() => {
                let rows = [];
                if (Array.isArray(data.rsvps) && data.rsvps.length > 0) {
                  rows = data.rsvps.map((r) => ({
                    id: r.userId,
                    name: r.userName || 'User',
                    status: (r.status || 'PENDING').toUpperCase(),
                    isOrganizer: sameId(r.userId, data.organizerId),
                  }));
                } else {
                  rows = (data.participants || []).map((p) => ({
                    id: p.id,
                    name: p.name || 'User',
                    status: (p.rsvpStatus || 'PENDING').toUpperCase(),
                    isOrganizer: sameId(p.id, data.organizerId),
                  }));
                }
                // Ensure current user appears so they can always respond
                if (user?.id && !rows.some((r) => sameId(r.id, user.id))) {
                  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'You';
                  rows = [
                    {
                      id: user.id,
                      name,
                      status: 'PENDING',
                      isOrganizer: sameId(data.organizerId, user.id),
                    },
                    ...rows,
                  ];
                }
                return rows;
              })();
              const rsvpLabel = {
                ACCEPTED: 'Accepted',
                REJECTED: 'Rejected',
                TENTATIVE: 'Tentative',
                PENDING: 'Waiting for response',
              };
              const rsvpBadge = {
                ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
                REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
                TENTATIVE: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
                PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
              };
              const canRespondRsvp = Boolean(user?.id) && data.status !== 'CANCELLED' && !data.linkedReminderId;
              return (
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{data.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTimeShort(data.startTime)} – {formatTimeShort(data.endTime)} · {data.status}
                      </p>
                      {data.linkedReminderId ? (
                        <p className="mt-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                          Busy time from reminder
                        </p>
                      ) : null}
                      {data.recurrenceType && data.recurrenceType !== 'none' ? (
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          Repeats {data.recurrenceType} for the next 1 month
                        </p>
                      ) : null}
                    </div>
                    <button type="button" onClick={() => setSelectedEvent(null)} className="text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {data.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-300">{data.description}</p>
                  )}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Participants</p>
                    <ul className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                      {rsvpRows.map((row) => {
                        const isMe = sameId(row.id, user?.id);
                        const hasDecided = Boolean(row.status && row.status !== 'PENDING');
                        const optionBtns = (
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                            <button
                              type="button"
                              onClick={() => handleRsvp(data.id, 'ACCEPTED')}
                              className={`text-[11px] font-semibold underline-offset-2 hover:underline ${
                                row.status === 'ACCEPTED'
                                  ? 'text-emerald-700 dark:text-emerald-300'
                                  : 'text-emerald-600 dark:text-emerald-400'
                              }`}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRsvp(data.id, 'TENTATIVE')}
                              className={`text-[11px] font-semibold underline-offset-2 hover:underline ${
                                row.status === 'TENTATIVE'
                                  ? 'text-sky-700 dark:text-sky-300'
                                  : 'text-sky-600 dark:text-sky-400'
                              }`}
                            >
                              Tentative
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRsvp(data.id, 'REJECTED')}
                              className={`text-[11px] font-semibold underline-offset-2 hover:underline ${
                                row.status === 'REJECTED'
                                  ? 'text-rose-700 dark:text-rose-300'
                                  : 'text-rose-600 dark:text-rose-400'
                              }`}
                            >
                              Reject
                            </button>
                          </div>
                        );
                        return (
                          <li
                            key={row.id || row.name}
                            className="group/rsvp px-2.5 py-1.5 text-xs"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-gray-800 dark:text-gray-200 font-medium min-w-0">
                                {row.name}
                                {isMe ? ' (you)' : ''}
                                {row.isOrganizer ? ' · organizer' : ''}
                              </span>
                              {/* Status badge always on the right */}
                              {(!isMe || hasDecided || !canRespondRsvp) && (
                                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  rsvpBadge[row.status] || rsvpBadge.PENDING
                                }`}>
                                  {rsvpLabel[row.status] || 'Waiting for response'}
                                </span>
                              )}
                            </div>
                            {/* Own RSVP actions on their own row so they aren't clipped */}
                            {isMe && canRespondRsvp && !hasDecided && (
                              <div className="mt-1 flex justify-end">
                                {optionBtns}
                              </div>
                            )}
                            {isMe && canRespondRsvp && hasDecided && (
                              <div className="mt-1 hidden justify-end group-hover/rsvp:flex">
                                {optionBtns}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {data.meetingLink && (
                    <a href={data.meetingLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all">
                      Join meeting
                    </a>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {data.status !== 'CANCELLED' && (isOrganizer || isParticipant) && (
                      <button
                        type="button"
                        onClick={() => openEditAppointment(data)}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-blue-600 text-white"
                      >
                        Edit
                      </button>
                    )}
                    {data.status !== 'CANCELLED' && (isOrganizer || isParticipant) && (
                      <button type="button" onClick={() => handleStatus(data.id, 'CANCELLED')} className="text-[11px] px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-600">
                        Cancel meeting
                      </button>
                    )}
                    {(isOrganizer || isParticipant) && (
                      <button type="button" onClick={() => handleDeleteAppointment(data.id)} className="text-[11px] px-2.5 py-1 rounded-md text-red-600">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            const isMine = data.userId === user?.id;
            const canBook = !isMine && data.status === 'AVAILABLE';
            return (
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {isMine ? 'Your availability' : `${data.userName || partnerName}'s availability`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTimeShort(data.startTime)} – {formatTimeShort(data.endTime)} · {data.status}
                    </p>
                  </div>
                  <button type="button" onClick={() => setSelectedEvent(null)} className="text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {canBook && (
                    <button
                      type="button"
                      onClick={() => handleBookPartnerSlot(data)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-blue-600 text-white"
                    >
                      Book this slot
                    </button>
                  )}
                  {isMine && data.status === 'AVAILABLE' && (
                    <button
                      type="button"
                      onClick={() => handleDeleteSlot(data.id)}
                      className="text-[11px] px-2.5 py-1 rounded-md text-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
            </div>
          )}
            </div>
          )}

      {/* DAY VIEW timeline */}
        <div className={`flex flex-col min-h-0 relative ${
          (draft || selectedEvent) ? 'w-[40%] flex-shrink-0' : 'flex-1'
        }`}
        >
          <button
            type="button"
            onClick={shiftTimelineUp}
            className="flex-shrink-0 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700/80"
            title="Earlier · 3 hours"
            aria-label="Earlier by 3 hours"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            3h earlier
          </button>

          {isDm ? (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-shrink-0 grid grid-cols-2 border-b border-gray-100 dark:border-gray-700/80">
                <div className="px-2 py-1.5 text-center text-[11px] font-semibold text-gray-700 dark:text-gray-200 border-r border-gray-100 dark:border-gray-700/80 truncate">
                  You
                </div>
                <div className="px-2 py-1.5 text-center text-[11px] font-semibold text-gray-700 dark:text-gray-200 truncate">
                  {partnerName}
                </div>
              </div>
              <div
                ref={gridRef}
                className="flex-1 overflow-y-auto min-h-0 relative select-none"
                onMouseDown={handleGridMouseDown}
              >
                <div className="relative flex" style={{ height: WINDOW_HOURS * HOUR_HEIGHT }}>
                  <div className="w-11 flex-shrink-0 relative">
                    {hourSlots.map((slotDate, idx) => (
                      <div
                        key={`lbl-${idx}`}
                        className="absolute left-0 right-0 -mt-2.5 pr-1 text-right"
                        style={{ top: idx * HOUR_HEIGHT }}
                      >
                        <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium">
                          {formatHourLabel(slotDate.getHours())}
                        </span>
                      </div>
                    ))}
                  </div>
                  {[
                    { id: user?.id, key: 'me', blocks: laidBlocksForUser(user?.id) },
                    { id: otherUser?.id, key: 'them', blocks: laidBlocksForUser(otherUser?.id) },
                  ].map((col, colIdx) => (
                    <div
                      key={col.key}
                      className={`flex-1 relative border-l border-gray-100 dark:border-gray-700/80 ${colIdx === 0 ? '' : ''}`}
                    >
                      {hourSlots.map((slotDate, idx) => (
                        <div
                          key={`${col.key}-h-${idx}`}
                          className="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-700/80"
                          style={{ top: idx * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                        />
                      ))}
                      {nowLineTop != null && (
                        <div
                          className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
                          style={{ top: nowLineTop }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          <span className="flex-1 h-0.5 bg-red-500" />
                        </div>
                      )}
                      <div className="absolute inset-x-0.5 top-0 bottom-0 z-10">
                        {col.blocks.map((block) => (
                          <button
                            key={`${col.key}-${block.key}`}
                            type="button"
                            data-cal-event
                            onClick={(e) => handleEventClick(block, e)}
                            onMouseDown={(e) => e.stopPropagation()}
                            className={`absolute rounded-md border px-1 py-0.5 text-left overflow-hidden shadow-sm hover:brightness-95 transition ${block.colorClass}`}
                            style={blockLayoutStyle(block)}
                            title={`${block.label} · ${block.sub}`}
                          >
                            <p className="text-[10px] font-semibold leading-tight truncate">{block.label}</p>
                          </button>
                        ))}
                        {colIdx === 0 && dragPreview && (
                          <div
                            className="absolute left-0 right-0 rounded-md border-2 border-blue-500 bg-blue-500/20 pointer-events-none"
                            style={{ top: dragPreview.top, height: dragPreview.height }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              ref={gridRef}
              className="flex-1 overflow-y-auto min-h-0 relative select-none"
              onMouseDown={handleGridMouseDown}
            >
              <div className="relative" style={{ height: WINDOW_HOURS * HOUR_HEIGHT }}>
                {hourSlots.map((slotDate, idx) => (
                  <div
                    key={`${slotDate.toISOString()}-${idx}`}
                    className="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-700/80 flex"
                    style={{ top: idx * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                  >
                    <div className="w-14 flex-shrink-0 -mt-2.5 pr-2 text-right">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                        {formatHourLabel(slotDate.getHours())}
                      </span>
                      {idx === 0 || slotDate.getHours() === 0 ? (
                        <span className="block text-[9px] text-gray-300 dark:text-gray-600 leading-tight">
                          {slotDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex-1 border-l border-gray-100 dark:border-gray-700/80" />
                  </div>
                ))}

                {nowLineTop != null && (
                  <div
                    className="absolute left-12 right-2 z-20 pointer-events-none flex items-center transition-[top] duration-500"
                    style={{ top: nowLineTop }}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1" />
                    <span className="flex-1 h-0.5 bg-red-500" />
                  </div>
                )}

                <div className="absolute top-0 bottom-0 left-14 right-2 z-10">
                  {laidOutEventBlocks.map((block) => (
                    <button
                      key={block.key}
                      type="button"
                      data-cal-event
                      onClick={(e) => handleEventClick(block, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={`absolute rounded-md border px-1.5 py-0.5 text-left overflow-hidden shadow-sm hover:brightness-95 transition ${block.colorClass}`}
                      style={blockLayoutStyle(block)}
                      title={`${block.label} · ${block.sub}`}
                    >
                      <p className="text-[11px] font-semibold leading-tight truncate">{block.label}</p>
                      {block.height > 28 && (
                        <p className="text-[10px] opacity-80 truncate">{block.sub}</p>
                      )}
                    </button>
                  ))}

                  {dragPreview && (
                    <div
                      className="absolute left-0 right-0 rounded-md border-2 border-blue-500 bg-blue-500/20 pointer-events-none"
                      style={{ top: dragPreview.top, height: dragPreview.height }}
                    />
                  )}

                  {draft && !selectedEvent && (() => {
                    const startMs = toDateTime(draft.date || toDateStr(windowStart), draft.startTime).getTime();
                    let endMs = toDateTime(draft.date || toDateStr(windowStart), draft.endTime).getTime();
                    if (endMs <= startMs) endMs += 24 * 60 * 60 * 1000;
                    const winStartMs = windowStart.getTime();
                    const winEndMs = windowEnd.getTime();
                    if (endMs <= winStartMs || startMs >= winEndMs) return null;
                    const clippedStart = Math.max(startMs, winStartMs);
                    const clippedEnd = Math.min(endMs, winEndMs);
                    return (
                      <div
                        className="absolute left-0 right-0 rounded-md border-2 border-blue-600 bg-blue-600/15 pointer-events-none z-30"
                        style={{
                          top: msToTop(clippedStart),
                          height: Math.max(((clippedEnd - clippedStart) / (60 * 60 * 1000)) * HOUR_HEIGHT, 22),
                        }}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={shiftTimelineDown}
            className="flex-shrink-0 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80"
            title="Later · 3 hours"
            aria-label="Later by 3 hours"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            3h later
          </button>
        </div>
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === 'week' && (
        <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-2">
          {weekDays.map((dateStr) => {
            const d = parseDateStr(dateStr);
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            const { appts, slots } = eventsForDate(dateStr);
            const count = appts.length + slots.length;
            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => {
                  openDayForDate(dateStr);
                }}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${
                  isSelected
                    ? 'border-blue-400 dark:border-blue-500 bg-blue-50/80 dark:bg-blue-950/30'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold flex-shrink-0 ${
                        isToday
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {d.getDate()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {WEEKDAY_SHORT[d.getDay()]}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {d.toLocaleDateString(undefined, { month: 'short' })}
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                    {count === 0 ? 'Free' : `${count} item${count === 1 ? '' : 's'}`}
                  </span>
                </div>
                {count > 0 && (
                  <div className="space-y-1 pl-10">
                    {appts.slice(0, 3).map((a) => (
                      <div key={a.id} className="flex items-center gap-1.5 text-[11px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="truncate text-gray-700 dark:text-gray-300">
                          {formatTimeShort(a.startTime)} {a.title}
                        </span>
                      </div>
                    ))}
                    {slots.filter((s) => s.status === 'AVAILABLE').slice(0, 2).map((s) => (
                      <div key={s.id} className="flex items-center gap-1.5 text-[11px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                        <span className="truncate text-gray-700 dark:text-gray-300">
                          {formatTimeShort(s.startTime)}–{formatTimeShort(s.endTime)} free
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW — only when Month is selected */}
      {viewMode === 'month' && (
        <div className="flex-1 overflow-y-auto min-h-0 p-3">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAY_SHORT.map((d) => (
              <div key={d} className="text-[10px] font-medium text-center text-gray-400 py-1">
                {d.slice(0, 1)}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((cell, idx) => {
              if (!cell) return <div key={`e-${idx}`} className="aspect-square" />;
              const dateStr = toDateStr(cell);
              const isToday = dateStr === todayStr;
              const hasEvents = datesWithEvents.has(dateStr);
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => {
                    openDayForDate(dateStr);
                  }}
                  className={`aspect-square rounded-lg text-xs font-medium relative flex flex-col items-center justify-center ${
                    isToday
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {cell.getDate()}
                  {hasEvents && (
                    <span
                      className={`absolute bottom-1 w-1 h-1 rounded-full ${
                        isToday ? 'bg-white' : 'bg-blue-500'
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[10px] text-center text-gray-400">
            Tap a date to open that day’s schedule
          </p>
        </div>
      )}


      <CreateEventWizard
        isOpen={showEventWizard}
        onClose={() => {
          setShowEventWizard(false);
          setWizardSchedulePreset(null);
        }}
        onCreated={() => {
          loadData();
        }}
        channel={channel}
        otherUser={otherUser}
        personalMode={isPersonal}
        schedulePreset={wizardSchedulePreset}
      />

      <style>{`
        .appt-quill .ql-toolbar.ql-snow {
          border: none;
          border-bottom: 1px solid rgba(167, 139, 250, 0.35);
          padding: 4px 6px;
        }
        .appt-quill .ql-container.ql-snow {
          border: none;
          font-size: 13px;
          min-height: 56px;
        }
        .appt-quill .ql-editor {
          min-height: 56px;
          padding: 6px 10px;
        }
        .appt-quill .ql-editor.ql-blank::before {
          color: rgba(139, 92, 246, 0.55);
          font-style: normal;
        }
        .dark .appt-quill .ql-editor {
          color: #f9fafb;
        }
        .dark .appt-quill .ql-stroke {
          stroke: #e9d5ff;
        }
        .dark .appt-quill .ql-fill {
          fill: #e9d5ff;
        }
        .appt-time-input {
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .appt-time-input::-webkit-datetime-edit,
        .appt-time-input::-webkit-datetime-edit-fields-wrapper,
        .appt-time-input::-webkit-datetime-edit-hour-field,
        .appt-time-input::-webkit-datetime-edit-minute-field,
        .appt-time-input::-webkit-datetime-edit-ampm-field {
          font-size: 1.25rem;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default CalendarPanel;
